import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslations } from '@/i18n/use-translations';
import { completeSignIn } from '@/lib/auth/session';

// StrictMode double-mounts effects in dev; the second exchange would burn
// the now-consumed code. Survives remount because the Set is module-scoped.
const exchangedCodes = new Set<string>();

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const t = useTranslations('pages.admin.callback');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = params.get('code');

    if (code === null || code.length === 0) {
      setError(t('missing-code'));

      return;
    }

    if (exchangedCodes.has(code)) {
      return;
    }

    exchangedCodes.add(code);

    (async () => {
      const outcome = await completeSignIn(code, params.get('state'));

      if (outcome.kind === 'success') {
        // Land directly on /workspaces. Navigating to '/' would trip the
        // index loader's `redirect('/workspaces')`, which causes React
        // Router to re-run every protected loader — including the root
        // requireSession → /me — twice.
        navigate('/workspaces', { replace: true });

        return;
      }

      if (outcome.kind === 'not-admin') {
        navigate('/access-denied', { replace: true });

        return;
      }

      setError(t('failed'));
    })().catch(() => {
      setError(t('failed'));
    });
  }, [params, navigate, t]);

  return (
    <main className="flex min-h-screen items-center justify-center bg-canvas p-6">
      <div className="space-y-4 text-center">
        {error === null ? (
          <p className="text-base text-text-tertiary">{t('loading')}</p>
        ) : (
          <>
            <p className="text-base text-danger-600">{error}</p>
            <a href="/sign-in" className="text-base text-interactive underline-offset-4 hover:underline">
              {t('back-to-sign-in')}
            </a>
          </>
        )}
      </div>
    </main>
  );
}
